import { useRouter } from 'next/router';
import React, { FC, forwardRef } from 'react';
import TableCell from './TableCell';
import { TableCellProps, TableRowProps } from './interfaces/Table';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const tableRowSpacingClasses = {
  tableRowPadding: 'p-0',
  tableRowGap: 'gap-sm-wholestep-dec',
};
const TableRow: FC<TableRowProps & { rightAlignLastColumn?: boolean }> =
  forwardRef<HTMLDivElement, TableRowProps>(
    (
      {
        cells,
        columnWidths,
        rowIndex,
        onClick,
        navigateToUrl,
        rightAlignLastColumn = true,
      },
      ref,
    ) => {
      const router = useRouter();
      const handleClick = (
        event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      ) => {
        if (navigateToUrl) {
          router.push(navigateToUrl);
        } else if (onClick) {
          onClick(event, rowIndex || 0);
        }
      };
      const hasUrlOrClick = navigateToUrl || onClick;
      const getGridTemplateColumns = (widths?: number[]) => {
        if (!widths || widths.length === 0) {
          return `repeat(auto-fit, 1fr)`;
        }
        const isEqualWidths = widths.every((width) => width === widths[0]);
        if (isEqualWidths) {
          return `repeat(${widths.length}, 1fr)`;
        }
        const totalWidth = widths.reduce((acc, curr) => acc + curr, 0);
        return widths
          .map((width) => `${(width / totalWidth).toFixed(2)}fr`)
          .join(' ');
      };
      return (
        <div
          ref={ref}
          className={`grid w-full 
          ${tableRowSpacingClasses.tableRowPadding} 
          ${tableRowSpacingClasses.tableRowGap} 
          ${borderClassToVisualizeElementsAndSpacing} 
          ${hasUrlOrClick ? `cursor-pointer group` : ''}`}
          style={{
            gridTemplateColumns: getGridTemplateColumns(columnWidths),
          }}
          onClick={hasUrlOrClick ? handleClick : undefined}
        >
          {cells.map((cell: TableCellProps, cellIndex) => {
            const isLastCell = cellIndex === cells.length - 1;
            const alignmentClass =
              cells.length === 1
                ? 'items-start text-left'
                : isLastCell
                ? rightAlignLastColumn
                  ? 'items-end text-right'
                  : 'items-start text-left'
                : cell.align === 'right'
                ? 'items-end text-right'
                : cell.align === 'center'
                ? 'items-center text-center'
                : 'items-start text-left';
            return (
              <div
                key={cellIndex}
                className={`flex flex-col 
                ${alignmentClass} 
                ${borderClassToVisualizeElementsAndSpacing}`}
              >
                <TableCell
                  mainText={cell.mainText}
                  subText={cell.subText}
                  mainTextSize={cell.mainTextSize}
                  mainTextColor={cell.mainTextColor}
                  subTextSize={cell.subTextSize}
                  subTextColor={cell.subTextColor}
                  leftIconUrl={cell.leftIconUrl}
                  leftIcon={cell.leftIcon}
                  leftIconWidth={cell.leftIconWidth}
                  leftIconHeight={cell.leftIconHeight}
                  leftIconBorderRadius={cell.leftIconBorderRadius}
                  rightIconUrl={cell.rightIconUrl}
                  rightIcon={cell.rightIcon}
                  align={
                    cells.length === 1
                      ? 'left'
                      : isLastCell
                      ? rightAlignLastColumn
                        ? 'right'
                        : 'left'
                      : cell.align
                  }
                  shouldMiddleAlignIconAndText={
                    cell.shouldMiddleAlignIconAndText
                  }
                  className={`${cell.className}`}
                />
              </div>
            );
          })}
        </div>
      );
    },
  );
export default TableRow;
